import { useEffect, useRef, useState } from 'react';
import type { KeyboardEvent } from 'react';
import { Search } from 'lucide-react';
import { useVlbSuche } from '@/hooks/useVlbSuche';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { Cover } from '@/components/ui/Cover';
import { isbnAnzeige } from '@/lib/isbn';
import { useToast } from '@/lib/toast';
import { ApiError } from '@/lib/api';
import type { VlbSuchTreffer } from '@/types/bestellung';
import { FOKUS_RING } from '@/lib/fokus';
import { melde } from '@/lib/nutzung';
import { DigitalMarke } from '@/components/ui/DigitalMarke';

type Props = {
  /** Bekommt die ISBN des gewaehlten Treffers — nackte Ziffern. */
  onWaehlen: (isbn: string) => void;
  /**
   * Suchbegriff, mit dem die Suche gleich startet — E-Book statt Buch:
   * „Gedruckte Ausgabe suchen" reicht den Titel des E-Books herein.
   */
  vorgabe?: string;
};

/** Untergrenze wie im Endpunkt (api/vlb/suche.php). */
const MIN_ZEICHEN = 3;

/**
 * Der zweite Weg zur Position: nach Titel, Autor oder Verlag suchen (#83).
 *
 * WARUM DIE SUCHE NICHT BEIM TIPPEN LAEUFT
 *   Das ISBN-Feld sucht seit A17 von selbst — dort gibt es einen
 *   Zeitpunkt, an dem die Eingabe fertig ist: die Nummer ist gueltig.
 *   Text hat diesen Zeitpunkt nicht. Eine Wartezeit feuerte mehrfach je
 *   getipptem Wort, und ob der VLB die Suche anders abrechnet als den
 *   Lookup, ist NICHT gemessen. Solange das so ist, fragt nur, wer
 *   drueckt: Enter oder Lupe.
 *
 * WARUM DER TREFFER NICHT DIREKT ZUR POSITION WIRD
 *   Die Suchantwort traegt keinen Preis (#83) und fuellt keinen Cache.
 *   Eine Position aus Suchdaten und eine aus dem Lookup waeren zwei
 *   verschieden gefuellte Zeilen fuer dieselbe Sache (Designprinzip 4).
 *   Deshalb reicht dieser Weg nur die ISBN weiter; alles Weitere macht
 *   der gewoehnliche ISBN-Weg.
 */
/**
 * E-Book statt Buch (03.11.2026): Digitale Ausgaben rutschen ans Ende.
 * Sie bleiben wählbar — sehr, sehr selten werden sie wirklich bestellt —,
 * aber die gedruckte Ausgabe soll zuerst im Blick liegen. Stabil: die
 * Reihenfolge des VLB bleibt innerhalb beider Gruppen erhalten.
 */
export function digitaleNachHinten<T extends { digital?: boolean }>(liste: readonly T[]): T[] {
  return [...liste].sort((a, b) => Number(a.digital === true) - Number(b.digital === true));
}

export function TitelSuche({ onWaehlen, vorgabe }: Props) {
  const [begriff, setBegriff] = useState(vorgabe ?? '');
  const [treffer, setTreffer] = useState<VlbSuchTreffer[] | null>(null);
  const feldRef = useRef<HTMLInputElement>(null);
  const suche = useVlbSuche();
  const toast = useToast();

  const zuKurz = begriff.trim().length < MIN_ZEICHEN;

  // Der Bereich entsteht erst, wenn jemand „Nach Titel suchen" gedrueckt
  // hat — der Fokus gehoert dann hierher. Auf dem Tablet spart das den
  // zweiten Tipp bis zur Tastatur.
  useEffect(() => {
    feldRef.current?.focus();
    // Mit Vorgabe gleich suchen: wer „Gedruckte Ausgabe suchen" drückt,
    // hat schon gesagt, wonach. Einmal beim Entstehen, nicht bei jedem
    // Tippen (siehe oben, warum die Suche nicht beim Tippen läuft).
    if (vorgabe && vorgabe.trim().length >= MIN_ZEICHEN) suchen();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function suchen(): void {
    const b = begriff.trim();
    if (b.length < MIN_ZEICHEN) {
      toast('error', `Bitte mindestens ${MIN_ZEICHEN} Zeichen eingeben.`);
      return;
    }
    // Die alte Liste faellt VOR der Anfrage, nicht erst mit der Antwort.
    // Sonst stuenden waehrend der Wartezeit die Treffer der vorigen
    // Frage unter dem neuen Begriff — und bei einem Fehlschlag blieben
    // sie stehen und saehen frisch aus (derselbe Fall wie A-01 an der
    // ISBN-Vorschau).
    setTreffer(null);
    suche.mutate(b, {
      onSuccess: (data) => {
        setTreffer(digitaleNachHinten(data.treffer));
        if (data.treffer.length === 0) melde('suche_ohne_treffer', { ort: 'erfassung_vlb', text: b });
      },
      onError: (err) => {
        if (err instanceof ApiError) {
          if (err.code === 'q_zu_kurz') {
            toast('error', `Bitte mindestens ${MIN_ZEICHEN} Zeichen eingeben.`);
            return;
          }
          if (err.code === 'vlb_rate_limit') {
            toast('error', 'VLB ist gerade überlastet. Gleich noch einmal versuchen.');
            return;
          }
        }
        toast('error', 'VLB nicht erreichbar.');
      }
    });
  }

  // Enter sucht. stopPropagation, weil der ISBN-Bereich daneben ein
  // eigenes Enter kennt (Scanner-Fluss, B50) — das darf hier nicht
  // mitgreifen.
  function handleKeyDown(e: KeyboardEvent<HTMLInputElement>): void {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    e.stopPropagation();
    suchen();
  }

  return (
    <div className="space-y-2" data-titelsuche>
      <div className="flex flex-wrap gap-2">
        <input
          ref={feldRef}
          type="text"
          value={begriff}
          maxLength={200}
          onChange={(e) => setBegriff(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Titel, Autor oder Verlag"
          aria-label="Nach Titel, Autor oder Verlag suchen"
          className={`min-h-[44px] min-w-0 flex-1 basis-full rounded-md border border-line bg-cream px-3 py-2.5 text-sm ${FOKUS_RING} md:basis-auto`}
        />
        {/* Die Lupe ist der ausdrueckliche Ausloeser. Sie traegt eine
            sichtbare Beschriftung: ein Symbol allein liesse offen, ob
            hier gesucht oder gefiltert wird — und auf dem Tablet gibt es
            keinen Titel-Text beim Darueberfahren. */}
        <Button
          variant="primary"
          className="flex-1 md:flex-none"
          onClick={suchen}
          disabled={suche.isPending || zuKurz}
        >
          {suche.isPending ? <Spinner /> : <Search className="h-4 w-4" />}
          Suchen
        </Button>
      </div>

      {treffer !== null && treffer.length === 0 && (
        <p className="rounded-md border border-line bg-cream-warm p-3 text-xs text-ink-soft">
          Keine Treffer. Das Verzeichnis lieferbarer Bücher führt vor allem
          den deutschsprachigen Buchhandel — mit einem kürzeren Suchbegriff
          oder über die ISBN kommst Du oft weiter.
        </p>
      )}

      {treffer !== null && treffer.length > 0 && (
        <>
          <p className="text-xs text-ink-faint">
            {treffer.length === 1 ? '1 Treffer' : `${treffer.length} Treffer`} — zum
            Übernehmen antippen.
          </p>
          {/* Zwei Ausgaben desselben Titels stehen beide da, mit ISBN und
              Jahr. Kein Zusammenfassen, keine Vorauswahl: welche Ausgabe
              gemeint ist, weiss nur der Mensch am Tresen. */}
          <ul className="max-h-80 space-y-1 overflow-y-auto">
            {treffer.map((t, i) => (
              <li key={t.vlb_product_id ?? t.isbn ?? i}>
                <button
                  type="button"
                  onClick={() => t.isbn && onWaehlen(t.isbn)}
                  disabled={t.isbn === null}
                  data-digital={t.digital ? 'ja' : undefined}
                  className={`flex min-h-[44px] w-full items-start gap-3 rounded-md border p-2 text-left ${FOKUS_RING} disabled:opacity-50 ${
                    t.digital
                      ? 'border-coral-soft bg-cream-warm opacity-80 hover:border-coral'
                      : 'border-line bg-cream hover:border-akzent'
                  }`}
                >
                  <Cover isbn={t.isbn} className="h-16 w-12" />
                  <span className="flex min-w-0 flex-col gap-0.5">
                    {t.digital && (
                      <span>
                        <DigitalMarke />
                      </span>
                    )}
                    <span className="font-serif text-sm text-ink">
                      {t.titel ?? 'Titel ohne Bezeichnung'}
                    </span>
                    {t.untertitel && (
                      <span className="text-xs text-ink-soft">{t.untertitel}</span>
                    )}
                    {t.autor && <span className="text-xs text-ink-soft">{t.autor}</span>}
                    <span className="text-xs text-ink-faint">
                      {[t.verlag_text, t.erscheinungsjahr].filter(Boolean).join(' · ')}
                    </span>
                    {t.isbn && (
                      <span className="tabular-nums text-xs text-ink-faint">
                        ISBN {isbnAnzeige(t.isbn)}
                      </span>
                    )}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}
